import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { FavouriteOrderItemModel, FavouriteOrderItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['favouriteOrderItems']

type FavouriteOrderItemFilters = {
  favourite_order_id?: number,
  options_string?: string,
  provider_id?: number,
  provider_price_list_id?: number,
  provider_price_list_item_id?: number,
  publication_edition_id?: number,
  publication_id?: number,
  publication_package_id?: number,
  quantity?: number,
  service_id?: number,
  service_product_id?: number,
  string?: string,
}

type UseFavouriteOrderItemsOptions = {
  filters?: FavouriteOrderItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: FavouriteOrderItemRequestOptions,
}

function useFavouriteOrderItems(options: UseFavouriteOrderItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    favourite_order_id: filterFavouriteOrderId,
    options_string: filterOptionsString,
    provider_id: filterProviderId,
    provider_price_list_id: filterProviderPriceListId,
    provider_price_list_item_id: filterProviderPriceListItemId,
    publication_edition_id: filterPublicationEditionId,
    publication_id: filterPublicationId,
    publication_package_id: filterPublicationPackageId,
    quantity: filterQuantity,
    service_id: filterServiceId,
    service_product_id: filterServiceProductId,
    string: filterString,
  } = filters

  const {
    updatedEntities: { favouriteOrderItems: favouriteOrderItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { favouriteOrderItems } = useSelector(reduxState => reduxState.entities)

  const filteredFavouriteOrderItems = useMemo(() => {
    const filtered = Object.values(favouriteOrderItems).filter((favouriteOrderItem: FavouriteOrderItemModel) => {
      const {
        favourite_order_id,
        id,
        options_string,
        provider_id,
        provider_price_list_id,
        provider_price_list_item_id,
        publication_edition_id,
        publication_id,
        publication_package_id,
        quantity,
        service_id,
        service_product_id,
        title,
      } = favouriteOrderItem

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchFavouriteOrderId = matchFilterNumber(favourite_order_id, filterFavouriteOrderId)
      const matchOptionsString = matchFilterString(options_string, filterOptionsString)
      const matchProviderId = matchFilterNumber(provider_id, filterProviderId)
      const matchProviderPriceListId = matchFilterNumber(provider_price_list_id, filterProviderPriceListId)
      const matchProviderPriceListItemId = matchFilterNumber(provider_price_list_item_id, filterProviderPriceListItemId)
      const matchPublicationEditionId = matchFilterNumber(publication_edition_id, filterPublicationEditionId)
      const matchPublicationId = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationPackageId = matchFilterNumber(publication_package_id, filterPublicationPackageId)
      const matchQuantity = matchFilterNumber(quantity, filterQuantity)
      const matchServiceId = matchFilterNumber(service_id, filterServiceId)
      const matchServiceProductId = matchFilterNumber(service_product_id, filterServiceProductId)

      return matchFavouriteOrderId
        && matchOptionsString
        && matchProviderId
        && matchProviderPriceListId
        && matchProviderPriceListItemId
        && matchPublicationEditionId
        && matchPublicationId
        && matchPublicationPackageId
        && matchQuantity
        && matchServiceId
        && matchServiceProductId
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [favouriteOrderItemsUpdatedAt, JSON.stringify(filters)])

  const filteredFavouriteOrderItemsCount = filteredFavouriteOrderItems.length
  const hasFilteredFavouriteOrderItems = !!filteredFavouriteOrderItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredFavouriteOrderItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingFavouriteOrderItems } = useReduxAction(
    'favouriteOrderItems',
    'loadFavouriteOrderItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredFavouriteOrderItems,
    filteredFavouriteOrderItemsCount,
    hasFilteredFavouriteOrderItems,
    loading: loadingFavouriteOrderItems,
  }
}

export default useFavouriteOrderItems
